import InfoCardBlock from 'components/common/form/custom/ReplicatedInfo/InfoCardBlock';
import { WebFormCustomFields } from 'store/ui-stores/web-form/webFormStore';
import { ReplicatedCustomInfo, ZywaveBravoHealthImqDoctor, ZywaveBravoHealthImqMedication } from 'types/formService';
import { DefaultFormFields, FormController, FormControl } from '@insurancemenu/imenu-components-lib/dist/utils/forms';
import { createStyles } from 'components/common/form/custom/ReplicatedInfo/InfoCardModal/styles';
import { InputText } from '@insurancemenu/imenu-components-lib';
import React from 'react';
import { validateDateThatNotInFuture } from '@insurancemenu/imenu-components-lib/dist/helpers/validateDateOfBirth';

const asDoctor = (info: ReplicatedCustomInfo): ZywaveBravoHealthImqDoctor => info as ZywaveBravoHealthImqDoctor;
const asMedication = (info: ReplicatedCustomInfo): ZywaveBravoHealthImqMedication =>
  info as ZywaveBravoHealthImqMedication;

export const getDoctors = (): JSX.Element => {
  return (
    <InfoCardBlock
      key="bravoHealthImqDoctor"
      componentName="bravoHealthImqDoctor"
      amountOfAdditionalCards={99}
      customModalHeaderName="Doctor"
      addButtonName="Add Doctor"
      getCustomComponentField={(customField: WebFormCustomFields) => {
        return customField.bravoHealthImqDoctor;
      }}
      columns={[
        {
          title: 'Name of Doctor',
          width: '30rem',
          getValue: (info) => asDoctor(info).doctorName ?? '',
        },
        {
          title: 'Specialty',
          width: '20rem',
          getValue: (info) => asDoctor(info).specialty ?? '',
        },
      ]}
      createCustomForm={(): FormController<DefaultFormFields> => {
        return new FormController<DefaultFormFields>({
          doctorName: new FormControl<string>('', { validations: ['validName'], trim: true }),
          specialty: new FormControl<string>('', { validations: ['printableCharacters'], trim: true }),
        });
      }}
      customModalContent={(customForm: FormController<DefaultFormFields>): JSX.Element => {
        const classes = createStyles();
        const doctorName = customForm.fields.doctorName;
        const specialty = customForm.fields.specialty;
        return (
          <>
            <InputText
              label="Name of Doctor"
              name="doctorName"
              maxLength={50}
              value={doctorName.value?.toString()}
              onChange={customForm.onFieldChange}
              validationMessage={doctorName.unpassedValidationMsg || ''}
              validationPassed={!doctorName.unpassedValidationMsg}
              className={classes.regularField}
            />
            <InputText
              label="Specialty"
              name="specialty"
              maxLength={50}
              value={specialty.value?.toString()}
              onChange={customForm.onFieldChange}
              validationMessage={specialty.unpassedValidationMsg || ''}
              validationPassed={!specialty.unpassedValidationMsg}
              className={classes.regularField}
            />
          </>
        );
      }}
    />
  );
};

export const getMedications = (): JSX.Element => {
  return (
    <InfoCardBlock
      key="bravoHealthImqMedication"
      componentName="bravoHealthImqMedication"
      amountOfAdditionalCards={99}
      customModalHeaderName="Medication"
      addButtonName="Add Medication"
      getCustomComponentField={(customField: WebFormCustomFields) => {
        return customField.bravoHealthImqMedication;
      }}
      columns={[
        {
          title: 'Name of Medication',
          width: '30rem',
          getValue: (info) => asMedication(info).medicationName ?? '',
        },
        {
          title: 'Refill Date',
          width: '20rem',
          getValue: (info) => asMedication(info).refillDate ?? '',
        },
      ]}
      createCustomForm={(): FormController<DefaultFormFields> => {
        return new FormController<DefaultFormFields>({
          medicationName: new FormControl<string>('', { validations: ['printableCharacters'], trim: true }),
          refillDate: new FormControl<string>('', {
            optional: false,
            customValidations: [
              {
                // Validation ensures the selected date is equal to or later than the current day.
                check: (value: string): boolean => validateDateThatNotInFuture(value),
                msg: 'Date cannot be in the future.',
              },
            ],
          }),
        });
      }}
      customModalContent={(customForm: FormController<DefaultFormFields>): JSX.Element => {
        const classes = createStyles();
        const medicationName = customForm.fields.medicationName;
        const refillDate = customForm.fields.refillDate;
        return (
          <>
            <InputText
              label="Name of Medication"
              name="medicationName"
              maxLength={50}
              value={medicationName.value?.toString()}
              onChange={customForm.onFieldChange}
              validationMessage={medicationName.unpassedValidationMsg || ''}
              validationPassed={!medicationName.unpassedValidationMsg}
              className={classes.regularField}
            />
            <InputText
              label="Refill Date"
              name="refillDate"
              id="refillDate"
              htmlType="date"
              value={refillDate.value?.toString()}
              onChange={customForm.onFieldChange}
              validationMessage={refillDate.unpassedValidationMsg || ''}
              validationPassed={!refillDate.unpassedValidationMsg}
            />
          </>
        );
      }}
    />
  );
};
